import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { checkMeiyeAmount } from '$/api/server';
import { createNodeExecuteTask, queryNodeExecuteTask } from '$/domains/node/nodeService';
import { toolsCountCost } from '$/domains/mcp/mcpService';
import { getTaskSpeed } from '$/store/useTaskSpeedStore';
import { getEffectiveNodeCost } from '$/store/useTeamFlowStore';
import { openVipModal } from '$/store/useVipModalStore';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import type { RoleGenFormValues } from './RoleSettingForm';
import type { RoleGenModelOption } from './useModelOptions';
import {
  buildRoleGenExecuteExtra,
  buildRoleGenNodeExecuteTask,
  buildRoleGenTaskParameters,
  ROLE_GEN_BUSINESS_TYPE,
  ROLE_GEN_COST_ITEM_ID,
} from './roleGenerateTask';
import {
  trackRoboneoAssetRoleRunClick,
  trackRoboneoAssetRoleRunFail,
  trackRoboneoAssetRoleRunSuccess,
} from '../../tracking';

/** 生成任务状态机；任务创建成功后继续用于预览和历史记录状态展示。 */
export type RoleGenStatus = 'idle' | 'generating' | 'succeeded' | 'failed';

/** 三视图视角。 */
export type RoleGenViewType = 'front' | 'side' | 'back';

/** 单张三视图图片。 */
export interface ThreeViewImage {
  url: string;
  originalUrl: string;
  watermarkUrl: string;
  view: RoleGenViewType;
}

interface UseRoleGenSubmitOptions {
  roomId: string;
  selectedModel?: RoleGenModelOption;
}

const ROLE_GEN_POLL_INTERVAL_MS = 3000;
const ROLE_GEN_POLL_MAX_COUNT = 120;
const ROLE_GEN_VIEW_ORDER: RoleGenViewType[] = ['front', 'side', 'back'];

/** 延迟轮询间隔，避免 query 接口被同步循环打满。 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** 将后端原图与水印图按三视图顺序转换为预览列表，默认 URL 保持优先使用水印结果。 */
export function buildThreeViewImages(
  watermarkUrls: string[],
  originalUrls: string[] = [],
): ThreeViewImage[] {
  const imageCount = Math.min(
    Math.max(watermarkUrls.length, originalUrls.length),
    ROLE_GEN_VIEW_ORDER.length,
  );
  return Array.from({ length: imageCount }, (_, index) => {
    const originalUrl = originalUrls[index] || watermarkUrls[index] || '';
    const watermarkUrl = watermarkUrls[index] || originalUrl;
    return {
      url: watermarkUrl,
      originalUrl,
      watermarkUrl,
      view: ROLE_GEN_VIEW_ORDER[index],
    };
  }).filter((image) => Boolean(image.url));
}

/** 轮询角色生成任务结果，成功后优先读取 last_image_urls。 */
async function pollRoleGenTaskImages(
  taskId: string,
  roomId: string,
): Promise<ThreeViewImage[]> {
  for (let count = 0; count < ROLE_GEN_POLL_MAX_COUNT; count += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await queryNodeExecuteTask([taskId], roomId);
    const task = result.tasks?.[taskId];
    const failedStep = task?.steps?.find((step) => step.state === 'FAIL');
    if (failedStep) {
      throw new Error(failedStep.error_message || failedStep.fail_code || '角色生成失败');
    }

    if (task?.last_image_urls?.length) {
      return buildThreeViewImages(task.last_image_urls, task.initial_transferred_urls);
    }

    // eslint-disable-next-line no-await-in-loop
    await sleep(ROLE_GEN_POLL_INTERVAL_MS);
  }
  throw new Error('角色生成超时，请稍后重试');
}

/** 创建角色执行前重新询价，保持按钮展示与真实扣费前置检查一致。 */
async function fetchRoleGenCost(values: RoleGenFormValues, selectedModel: RoleGenModelOption, roomId: string) {
  const parameters = buildRoleGenTaskParameters(values, selectedModel);
  const result = await toolsCountCost({
    room_id: roomId,
    task_speed: getTaskSpeed(),
    items: [{
      id: ROLE_GEN_COST_ITEM_ID,
      tool_name: selectedModel.apiName,
      params: parameters,
    }],
  });
  const item = result.items.find((costItem) => costItem.id === ROLE_GEN_COST_ITEM_ID);
  return {
    cost: item?.cost ?? 0,
    ...(typeof item?.discount_cost === 'number' ? { discount_cost: item.discount_cost } : {}),
  };
}

/**
 * 生成任务提交 hook，复用画布 countcost / nodeexecute / nodeexecutequery 链路。
 *
 * @returns 状态机 + 提交/重置方法
 */
export default function useRoleGenSubmit({
  roomId,
  selectedModel,
}: UseRoleGenSubmitOptions): {
    status: RoleGenStatus;
    errorMsg: string | null;
    previewImages: ThreeViewImage[];
    taskId: string;
    submittedAt: number;
    submit: (values: RoleGenFormValues, nodeExtraValues?: RoleGenFormValues) => Promise<void>;
    reset: () => void;
  } {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const [status, setStatus] = useState<RoleGenStatus>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [previewImages, setPreviewImages] = useState<ThreeViewImage[]>([]);
  const [taskId, setTaskId] = useState('');
  const [submittedAt, setSubmittedAt] = useState(0);
  const mountedRef = useRef(true);
  const activeRunRef = useRef(0);

  /**
   * 组件卸载或当前生成批次已失效时不触发 setState，避免关闭弹框后轮询结果回写旧状态。
   *
   * @param setter React state setter
   * @param value 下一次写入值
   * @param runId 当前生成批次 id
   */
  function setSafely<T>(setter: (v: T) => void, value: T, runId?: number): void {
    if (!mountedRef.current) return;
    if (runId !== undefined && activeRunRef.current !== runId) return;
    setter(value);
  }

  /** 校验余额并创建角色任务，预处理参数在请求前最后一步写入节点任务。 */
  const submit = useCallback(async (values: RoleGenFormValues, nodeExtraValues = values): Promise<void> => {
    if (!roomId || !selectedModel?.apiName) {
      message.error(languageStore.smartcatTranslationText(
        (value) => value.generation_config_error_retry_later,
      ));
      return;
    }

    const runId = activeRunRef.current + 1;
    activeRunRef.current = runId;
    setStatus('generating');
    setErrorMsg(null);
    setPreviewImages([]);
    setSubmittedAt(Date.now());
    setTaskId('');

    let createdTaskId = '';
    let inputParamsForTrack: Record<string, unknown> | undefined;
    try {
      const [amountResult, costInfo] = await Promise.all([
        checkMeiyeAmount(),
        fetchRoleGenCost(values, selectedModel, roomId),
      ]);
      if (amountResult.amount < getEffectiveNodeCost(costInfo)) {
        openVipModal('workflow_func');
        throw new Error(languageStore.smartcatTranslationText((value) => value.insufficient_credits));
      }

      const nodeId = `${ROLE_GEN_BUSINESS_TYPE}_${Date.now()}`;
      const taskParams = buildRoleGenNodeExecuteTask(values, selectedModel, nodeId, nodeExtraValues);
      inputParamsForTrack = taskParams.parameters as Record<string, unknown>;
      const taskResult = await createNodeExecuteTask({
        room_id: roomId,
        params: {
          ...taskParams,
          pre_tools: [
            {
              name: 'script_generate',
              parameters: {
                tool_knowledge_id: 300,
                summary: taskParams.parameters.prompt,
                image_list: taskParams.parameters.image_list,
                ratio: taskParams.parameters.ratio,
              },
            },
          ],
          pre_type: 'prompt_optimize',
        },
        workflow_version: 'v2',
        business_type: ROLE_GEN_BUSINESS_TYPE,
        extra: buildRoleGenExecuteExtra(),
        taskSpeed: getTaskSpeed(),
      });
      const [createdTask] = Array.isArray(taskResult) ? taskResult : [taskResult];
      if (!createdTask?.task_id) {
        throw new Error('角色生成任务创建失败');
      }

      createdTaskId = createdTask.task_id;
      trackRoboneoAssetRoleRunClick({
        roomId,
        values: nodeExtraValues,
        cost: String(getEffectiveNodeCost(costInfo)),
        taskId: createdTaskId,
      });
      setSafely(setTaskId, createdTaskId, runId);
      const images = await pollRoleGenTaskImages(createdTaskId, roomId);
      trackRoboneoAssetRoleRunSuccess({
        roomId,
        values: nodeExtraValues,
        resultImages: images,
        taskId: createdTaskId,
        inputParams: inputParamsForTrack,
      });
      setSafely(setPreviewImages, images, runId);
      setSafely(setStatus, 'succeeded', runId);
    } catch (error) {
      const insufficientCreditsText = languageStore.smartcatTranslationText((value) => value.insufficient_credits);
      const generationFailedText = languageStore.smartcatTranslationText(
        (value) => value.generation_failed_retry_later,
      );
      const rawErrorMessage = error instanceof Error ? error.message : generationFailedText;
      const displayErrorMessage = rawErrorMessage === insufficientCreditsText
        ? rawErrorMessage
        : generationFailedText;
      if (rawErrorMessage !== insufficientCreditsText) {
        trackRoboneoAssetRoleRunFail({
          roomId,
          values: nodeExtraValues,
          taskId: createdTaskId,
          inputParams: inputParamsForTrack,
          failInfo: rawErrorMessage,
        });
      }
      setSafely(setErrorMsg, displayErrorMessage, runId);
      setSafely(setStatus, 'failed', runId);
      if (rawErrorMessage !== insufficientCreditsText) {
        message.error(generationFailedText);
      }
    }
  }, [languageStore, roomId, selectedModel]);

  const reset = useCallback((): void => {
    activeRunRef.current += 1;
    setStatus('idle');
    setErrorMsg(null);
    setPreviewImages([]);
    setTaskId('');
    setSubmittedAt(0);
  }, []);

  // 卸载后禁止 setState，避免 SSE/轮询回调污染
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  return {
    status, errorMsg, previewImages, taskId, submittedAt, submit, reset,
  };
}
